/**
 *
 * Add Ariksa Outpost
 *
 */

import React, { FC, useEffect, useReducer } from 'react';

import { CloudProviders } from '@ariksa/cloud-account/api';
import { Box, HStack, Stack, Wrap } from '@chakra-ui/react';
import { filter, includes, isEmpty, map } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { Api } from 'api/index';
import { Form, Select } from 'components/DataEntry';
import {
  AwsIcon,
  Card,
  DetailsIdentifier,
  DocumentsIcon,
  PageHeaderWithIcon,
  Tag,
  WarningAlert,
} from 'components/index';
import { stopSignWithMessage } from 'containers/Setup/CloudAccounts/CloudAccountWizard/Components/utils/utils';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { selectOutpost } from 'containers/Setup/Outpost/selectors';
import { actions } from 'containers/Setup/Outpost/slice';
import { selectSharedState } from 'containers/SharedState/selectors';
import { actions as sharedStateActions } from 'containers/SharedState/slice';

import { formStyles } from '../../../../CloudAccounts/CloudAccountWizard/styles';
import { OutpostInfoBox } from '../../utils';

export const AddAccountForVulnerability: FC = () => {
  const navigate = useNavigate();
  const { scanningConfig, outpostActions, ariksaOutposts } = useSelector(
    selectOutpost,
  );
  const { regionsByProvider } = useSelector(selectSharedState);
  const dispatch = useDispatch();
  const { accountOptions } = useCloudAccountId();

  const [state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      accountOptions: [],
      regionOptions: [],
      selectedAccount: {},
      selectedRegions: [],
      isConfirmed: false,
      saved: false,
    },
  );

  useEffect(() => {
    dispatch(actions.getAriksaOutposts({ q: {} }));
  }, [dispatch]);

  useEffect(() => {
    const outpostAccounts = map(ariksaOutposts.data, o => o.id);
    const options = filter(
      accountOptions([CloudProviders.Aws], false),
      a => !includes(outpostAccounts, a.value),
    );
    updateState({
      accountOptions: options,
      selectedAccount: options[0],
    });
  }, [accountOptions, ariksaOutposts.data]);

  //get config
  useEffect(() => {
    if (!!state.selectedAccount?.value)
      dispatch(
        actions.getScanningConfig({
          q: {
            cloudType: CloudProviders.Aws,
          },
          onSuccess: res => {
            if (!!res?.aws_config?.account_id) {
              dispatch(
                actions.getEnabledRegionsForScanning({
                  q: { provider: CloudProviders.Aws },
                }),
              );
            }
          },
        }),
      );
  }, [dispatch, state.selectedAccount]);

  useEffect(() => {
    if (!!state.selectedAccount?.value)
      dispatch(
        sharedStateActions.getRegionsByProvider({
          q: {
            provider: state.selectedAccount?.data?.cloud_type?.toUpperCase(),
          },
          onSuccess: res => {
            let options: Record<string, any>[] = map(res, o => ({
              label: o.code,
              value: o.code,
            }));
            updateState({
              regionOptions: options,
            });
          },
        }),
      );
  }, [dispatch, state.selectedAccount]);

  const handleEnableRegions = () => {
    dispatch(
      actions.updateEnabledRegionsForScanning({
        q: {
          orgEnabledRegionsRequest: {
            regions: state.selectedRegions.map(o => o.value),
            provider: CloudProviders.Aws,
            account_id: state.selectedAccount?.value,
          },
        },
        onSuccess: () => {
          updateState({ saved: true });
        },
      }),
    );
  };

  const handleSubmit = data => {
    const { aws_config } = scanningConfig.data;
    const parameters: Record<string, any> = {
      stackName: `Ariksa-Vulnerability-Scanner-Role-${uuidv4()}`,
      param_ExternalID: aws_config?.external_id,
      templateURL: aws_config?.cfs_template_url,
      param_AriksaAdminAccountId: aws_config?.account_id,
    };

    const redirectUrl = Api.awsOnboardRedirectURL(parameters);

    navigate('/setup/outpost');
    window.open(redirectUrl, '_blank');

    dispatch(
      actions.getAriksaOutposts({
        q: {},
      }),
    );
  };

  const handleReset = () => {
    navigate('/setup/outpost');
  };

  const renderEnabledRegions = () => (
    <Stack spacing={4}>
      <OutpostInfoBox w="full" px={1} pb={2}>
        <DetailsIdentifier
          label="Regions enabled"
          value={
            <Select
              placeholder="Select regions to enable"
              options={state.regionOptions}
              value={state.selectedRegions}
              onChange={v => updateState({ selectedRegions: v, saved: false })}
              isLoading={regionsByProvider.isLoading}
              isDisabled={regionsByProvider.isLoading}
              isMulti
              hideSelectedOptions
              menuPortalTarget={document.body}
              showBorder={false}
              showTotalSelected
              showIconInValueContainer={false}
            />
          }
          isInline={false}
          styles={{ label: { px: 2, fontSize: '15px' } }}
          spacing={0}
        />
      </OutpostInfoBox>
      <Wrap>
        {map(state.selectedRegions, o => (
          <Tag
            label={o.label}
            leftIcon={<AwsIcon />}
            closeButton
            value={o.value}
            onClose={value => {
              updateState({
                selectedRegions: state.selectedRegions.filter(
                  r => r.value !== value,
                ),
                saved: false,
              });
            }}
          />
        ))}
      </Wrap>
    </Stack>
  );

  return (
    <Card>
      <Box w="full" h="full" pl={36}>
        <Form
          title={
            <PageHeaderWithIcon
              label="Add Account For Vulnerability Management"
              icon={<AwsIcon />}
              iconFilled={false}
            />
          }
          schema={{
            message: {
              type: 'custom',
              component: () => (
                <Box pb={4}>
                  <WarningAlert
                    message="Ariksa will scan all selected regions for this
                    account if there are resources provisioned in a particular
                    region. You’ll be redirected to AWS console to create a
                    cross-account role for Ariksa"
                    icon={<DocumentsIcon color="primary" />}
                  />
                </Box>
              ),
            },
            account: {
              type: 'custom',
              component: () => (
                <HStack spacing={4}>
                  <OutpostInfoBox w="full" pl={2}>
                    <DetailsIdentifier
                      label="Account"
                      value={
                        <Select
                          options={state.accountOptions}
                          value={state.selectedAccount}
                          onChange={v =>
                            updateState({ selectedAccount: v, saved: false })
                          }
                          menuPortalTarget={document.body}
                          showBorder={false}
                          showIconInValueContainer={false}
                          showClearIndicator={false}
                        />
                      }
                      styles={{ label: { px: 2, fontSize: '15px' } }}
                      isInline={false}
                    />
                  </OutpostInfoBox>
                  <OutpostInfoBox w="full" pb={3}>
                    <DetailsIdentifier
                      label="Cloud ID"
                      value={state.selectedAccount?.data?.cloud_account_id}
                      isInline={false}
                      spacing={1}
                    />
                  </OutpostInfoBox>
                </HStack>
              ),
            },
            region: {
              type: 'custom',
              component: renderEnabledRegions,
            },
            ...(state.saved
              ? {
                  confirmation: {
                    type: 'custom',
                    component: () => (
                      <Stack pt={2}>
                        <OutpostInfoBox py={4} px={5}>
                          {stopSignWithMessage(
                            'Review requirements before proceeding further',
                            'Confirm that you have permissions to create a cross-account role',
                            state.isConfirmed,
                            value => updateState({ isConfirmed: value }),
                          )}
                        </OutpostInfoBox>
                      </Stack>
                    ),
                  },
                }
              : {}),
          }}
          buttonOptions={{
            submit: {
              name: state.saved ? 'CONNECT' : 'SAVE',
              isLoading: outpostActions.isLoading,
              isDisabled:
                outpostActions.isLoading ||
                (state.saved && !state.isConfirmed) ||
                isEmpty(state.selectedRegions),
            },
            reset: {
              name: 'CANCEL',
              isVisible: true,
              onClick: handleReset,
            },
          }}
          styles={formStyles()}
          handleSubmit={state.saved ? handleSubmit : handleEnableRegions}
        />
      </Box>
    </Card>
  );
};
